// views/appcopy/index.tsx — App → App Copy. Every word the Detox Tracker app
// says, edited against a live picture of the screen it lands on.
//
// Three columns: the screen rail, the phone, the copy panel. Type in the panel and
// the phone updates on the keystroke; click a string on the phone and the panel
// jumps to its field. Spec: docs/APP-CONTENT-EDITOR.md + the App Copy design
// handoff.
//
// 🔴 THE PUBLISH GATE. Typing changes NOTHING for a customer — it saves to the
// working copy in D1 (so a half-finished edit survives a reload and still can't
// leak). A line reaches the app only when it's ticked Approved AND Publish is
// pressed, which copies it to `published` and bumps the version the app fetches.
// Publishing then CLEARS the tick, so a ticked box always means "waiting to go out".
//
// Published wording is PERMANENT: it lives in `published` here and in each phone's
// own cache, so an outage in this dashboard changes nothing a customer sees. The
// app's built-in wording is the floor only for lines that were never published.
//
// Two rules are enforced on save (here for speed, again on the server for real):
// dropping a {PLACEHOLDER} is BLOCKED — the app fills those in and losing one
// leaves a hole in the sentence; un-capsing an ALL CAPS line only WARNS, because
// the app ships exactly what you type and that's sometimes intended.

import React from "react";
import * as ReactDOM from "react-dom";
import { OSWALD, FIGTREE, MONO, useT, useIsMobile } from "../../theme";
import { Icon, Card, Btn, Title } from "../../atoms";
import { SCREENS, FLOW_ORDER, entriesForKey, placementIndex, keysInBlocks, type ScreenDef, type StateDef } from "./screens";
import { LiveFrame, previewUrl, DEVICES, type DeviceId, type PickInfo } from "./liveframe";
import { Phone, APP, VARS, type PhoneCtx, type AppTheme } from "./phone";

// The real programme, straight from the tracker repo (mobile/src/lib/protocol.ts +
// theme.ts, read 2026-08-23): a LOCKED sequence, Liver → Kidney → Parasite → Heavy
// Metal, with the days and hero palettes the app actually uses. Heavy Metal never
// takes the Binder; the others take it every other day from day 2, which is why the
// binder switch also moves the preview to an even day.
//
// Hero art is the app's own transparent 800×800 renders, copied into public/appcopy —
// including the binder-day combo shots the app swaps in. A synced manifest can point
// `hero` somewhere else, but this is the real thing until then.
export type Detox = {
  id: string; name: string; short: string; days: number; binder: boolean; n: number;
  hero: string; combo?: string; light: string; jar: string; deep: string; onHero: string;
};
const DETOXES: Detox[] = [
  { id: "liver", name: "Liver Cleanse", short: "Liver", days: 14, binder: true, n: 1,
    hero: "/appcopy/hero-liver.png", combo: "/appcopy/combo-liver.png",
    light: "#f9dc63", jar: "#f2c528", deep: "#dda205", onHero: "#3a2c00" },
  { id: "kidney", name: "Kidney Cleanse", short: "Kidney", days: 14, binder: true, n: 2,
    hero: "/appcopy/hero-kidney.png", combo: "/appcopy/combo-kidney.png",
    light: "#b8ecfb", jar: "#9ae0f5", deep: "#6cc9ea", onHero: "#0a3f4f" },
  { id: "parasite", name: "Parasite Killer", short: "Parasite", days: 30, binder: true, n: 3,
    hero: "/appcopy/hero-parasite.png", combo: "/appcopy/combo-parasite.png",
    light: "#ff7a6b", jar: "#ff5b4a", deep: "#e93b2c", onHero: "#ffffff" },
  { id: "metal", name: "Heavy Metal Detox", short: "Heavy Metal", days: 30, binder: false, n: 4,
    hero: "/appcopy/hero-metal.png",
    light: "#cbe6ce", jar: "#b4d6b8", deep: "#93c199", onHero: "#2b5230" },
];

type Row = {
  key: string; screen: string; where: string; when: string;
  text: string; published: string | null; builtin: string | null;
  notes: string | null; approved: boolean; retired?: boolean; erasable?: boolean;
  refused?: string | null;   // the app is IGNORING what's published here
  updated_at: string;
};
type Meta = { version: number; published_at: string | null; published_by: string | null };
/** The last time the app's string table was pulled, and what it did (worker: SeedSync). */
type SyncInfo = {
  at: string; ok: boolean; reason: string; forced?: boolean; version?: string | null;
  inserted?: number; updated?: number; retired?: number; total?: number;
};

const liveOf = (r: Row) => r.published ?? r.builtin ?? "";
const isChanged = (r: Row, value = r.text) => value !== liveOf(r);
const isPending = (r: Row) => r.approved && isChanged(r);

/**
 * SEARCH MATCHES WHAT HE TYPES, NOT HOW IT WAS TYPOGRAPHED.
 *
 * Dustin, 2026-09-13: searched for `account.reminders.caption`'s wording and found nothing. The
 * app writes curly quotes and em dashes (We’ll, time — each); a keyboard types We'll and -. Fold
 * both sides to plain before comparing, and look in every version of the line: the working copy,
 * what's published, and the app's own wording (a fix list quotes the app's NEW text, which is in
 * none of the other two).
 */
const fold = (s: string | null | undefined) =>
  (s || "").toLowerCase().replace(/[‘’ʼ`]/g, "'").replace(/[“”]/g, '"').replace(/[—–]/g, "-").replace(/\s+/g, " ");
const rowMatches = (r: Row, needle: string) =>
  !needle || [r.key, r.text, r.published, r.builtin, r.where, r.screen].some((s) => fold(s).includes(needle));

const placeholdersOf = (s: string) => Array.from(new Set(s.match(/\{[A-Za-z0-9_]+\}/g) || [])).sort();
const isAllCaps = (s: string) => /[A-Za-z]/.test(s) && s === s.toUpperCase();

// "Bottom card — button" → group "Bottom card", label "Button".
function splitWhere(where: string): { group: string; label: string } {
  const i = where.indexOf("—");
  if (i < 0) return { group: "General", label: where };
  const group = where.slice(0, i).trim();
  const label = where.slice(i + 1).trim();
  return { group: group || "General", label: (label || where).replace(/^./, (c) => c.toUpperCase()) };
}

function ago(iso: string | null): string {
  if (!iso) return "never";
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms)) return "never";
  const m = Math.round(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} hr${h === 1 ? "" : "s"} ago`;
  return `${Math.round(h / 24)} days ago`;
}

// 🔴 THE SAME PLACEHOLDER DOESN'T ALWAYS MEAN THE SAME THING. Most keys use {TOTAL}
// for "days in this detox" and {DAY} for the day number — but a few don't, and the
// preview filling them the usual way renders a plausible, wrong number. That is how
// "DETOX 1 OF 14" ended up on screen (read as 14 detoxes; the app meant 14 days) and
// why the tracker renamed that one to {COUNT} on 2026-08-23.
//
// Verified against the app's source, not guessed:
//   dock.scheduled        DAY   = fmtWeekday(startsOn)  → a weekday name
//   order.eyebrow.multi   N/TOTAL = index+1 of groups.length → new orders, NOT days
// Anything not listed here uses the normal bindings.
const KEY_VARS: Record<string, Record<string, string>> = {
  "dock.scheduled": { DAY: "Tuesday" },
  "order.eyebrow.multi": { N: "2", TOTAL: "3" },
};

// What the app would substitute into {BRACES} for this detox, on this kind of day.
function varsFor(d: Detox, binderDay: boolean): Record<string, string> {
  return {
    ...VARS,
    DETOX: d.name, ITEM: d.name, CURRENT: d.name,
    DAY: d.binder && binderDay ? "4" : "3", TOTAL: String(d.days), DAYS: String(d.days),
    N: String(d.n), COUNT: "4",
    BINDER: d.binder && binderDay ? " · binder day" : "",
    // the app formats these; these stand-ins just have to look like the real thing
    TIME: "7:34 AM",
    WHAT: d.binder && binderDay ? "Detox and binder both logged. " : "Detox logged. ",
    AFTER: " after your " + d.name,
  };
}

// ============================================================ SCREEN
export function AppCopyView() {
  const t = useT();
  const isMobile = useIsMobile(1100);

  const [rows, setRows] = React.useState<Row[] | null>(null);
  const [meta, setMeta] = React.useState<Meta | null>(null);
  const [sync, setSync] = React.useState<SyncInfo | null>(null);
  const [entryId, setEntryId] = React.useState<string>("today");
  const [stateIdx, setStateIdx] = React.useState(0);
  const [sel, setSel] = React.useState<string | null>(null);
  const [markers, setMarkers] = React.useState(false);
  const [detoxId, setDetoxId] = React.useState("liver");
  const [binderDay, setBinderDay] = React.useState(true);
  const [layout, setLayout] = React.useState<{ manifest: any; app_version: string | null; received_at: string; preview_base?: string | null } | null>(null);
  const [q, setQ] = React.useState("");
  const [edits, setEdits] = React.useState<Record<string, string>>({});      // unsaved keystrokes
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [saving, setSaving] = React.useState<Record<string, boolean>>({});
  const [busy, setBusy] = React.useState<string | null>(null);
  const [confirm, setConfirm] = React.useState<null | "publish" | "discard">(null);
  const [help, setHelp] = React.useState(false);
  const [toast, setToast] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    const d = await fetch("/api/app-content").then((r) => (r.ok ? r.json() : null)).catch(() => null);
    if (!d) { setRows([]); return; }
    setRows(d.rows || []);
    setMeta(d.meta || null);
    setSync(d.sync || null);
  }, []);
  React.useEffect(() => { load(); }, [load]);

  // The app's own layout + theme, if the tracker has pushed a manifest. Until it
  // does, the editor draws its own approximation and says so under the phone.
  React.useEffect(() => {
    fetch("/api/app-layout").then((r) => (r.ok ? r.json() : null))
      .then((d) => setLayout(d?.layout ? { ...d.layout, preview_base: d.preview_base ?? null } : null)).catch(() => {});
  }, []);
  React.useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(null), 3400);
    return () => clearTimeout(id);
  }, [toast]);

  const all = rows || [];

  /**
   * Every line as it stands in the editor RIGHT NOW — including edits typed but not
   * published. This is what the live preview renders, and the reason it's a preview rather
   * than a viewer: he sees his wording on the real screen before committing to it.
   * Retired keys are excluded; the app no longer asks for them.
   */
  const drafts = React.useMemo(() => {
    const out: Record<string, string> = {};
    for (const r of all) if (!r.retired && typeof r.text === "string") out[r.key] = r.text;
    return out;
  }, [all]);
  const byKey = React.useMemo(() => {
    const m = new Map<string, Row>();
    for (const r of all) m.set(r.key, r);
    return m;
  }, [all]);

  // A manifest may carry a theme, screens, or both — the tracker ships the theme
  // first. `screens` omitted (not []) means "keep your own layouts", so the two halves
  // are tracked separately and the caption says which is which.
  const synced = layout?.manifest?.theme || layout?.manifest?.screens?.length ? layout : null;
  const syncedScreens = synced?.manifest?.screens?.length ? synced : null;
  const srcScreens: ScreenDef[] = syncedScreens ? (syncedScreens.manifest.screens as ScreenDef[]) : SCREENS;

  // The app also sends each detox's real facts — name, days, whether it takes the
  // binder — off its protocol engine. Binding {DETOX}/{TOTAL} from that instead of
  // inferring them is what stops another "Detox 1 of 14"; metal.binder = false is the
  // single rule most likely to be got wrong by hand.
  const detoxes: Detox[] = React.useMemo(() => {
    const fromApp = synced?.manifest?.theme?.detox;
    if (!fromApp) return DETOXES;
    return DETOXES.map((d) => {
      const a = fromApp[d.id];
      if (!a) return d;
      return {
        ...d,
        name: a.name ?? d.name,
        short: a.chip ?? d.short,
        days: typeof a.days === "number" ? a.days : d.days,
        binder: typeof a.binder === "boolean" ? a.binder : d.binder,
        light: a.light ?? d.light, jar: a.jar ?? d.jar, deep: a.deep ?? d.deep, onHero: a.onHero ?? d.onHero,
      };
    });
  }, [synced]);

  const detox = detoxes.find((d) => d.id === detoxId) || detoxes[0];

  // Palette: the app's own tokens when synced, else this screen's placeholder,
  // tinted to whichever detox is being previewed.
  const theme: AppTheme = React.useMemo(() => {
    const t = synced?.manifest?.theme;
    return {
      ...APP,
      ...(t?.colors || {}),
      // the binder carries its own palette, used by the binder-tone buttons
      binderPurple: t?.binder?.jar || t?.binder?.mid || APP.binderPurple,
      light: detox.light, jar: detox.jar, deep: detox.deep, onHero: detox.onHero,
    };
  }, [synced, detox]);

  // Assign every row to the rail entries that show it — see entriesForKey for the rules and
  // why a key the layout DRAWS on a screen is always editable from that screen. Anything no
  // layout claims lands in a per-screen "Other copy" entry, so no string is ever unreachable.
  const { entries, rowsFor } = React.useMemo(() => {
    const map = new Map<string, Row[]>();
    const extra = new Map<string, ScreenDef>();
    const placed = placementIndex(srcScreens);
    const add = (id: string, r: Row) => {
      const list = map.get(id) || [];
      list.push(r);
      map.set(id, list);
    };
    for (const r of all) {
      // A key the app has dropped is kept (never deleted — it may carry an edit) but
      // parked out of the way, so the screens only show wording that still exists.
      if (r.retired) {
        if (!extra.has("retired")) {
          extra.set("retired", {
            id: "retired", label: "No longer in the app", flow: "Everywhere", screen: "", states: [],
            note: "The app stopped using these strings. They're kept in case you want the wording back, and publishing them does nothing.",
          });
        }
        add("retired", r);
        continue;
      }
      const ids = entriesForKey(r.screen, r.key, srcScreens, placed);
      if (ids.length) {
        for (const id of ids) add(id, r);
        continue;
      }
      const id = `other:${r.screen}`;
      if (!extra.has(id)) {
        extra.set(id, {
          id, label: `${r.screen} · other copy`, flow: "Everywhere", screen: r.screen, states: [],
          note: "No preview drawn for these yet — they're still fully editable.",
        });
      }
      add(id, r);
    }
    const defs = [...srcScreens, ...extra.values()].filter((s) => (map.get(s.id) || []).length);
    return { entries: defs, rowsFor: map };
  }, [all, srcScreens]);

  const entry = entries.find((e) => e.id === entryId) || entries[0] || null;
  /**
   * Lines the LIVE preview proved are on a screen whose layout doesn't list them — see
   * pickFromPreview. Per rail entry, for this session; they show in that entry's panel like
   * any other field.
   */
  const [adopted, setAdopted] = React.useState<Record<string, string[]>>({});
  const entryRows = React.useMemo(() => {
    if (!entry) return [] as Row[];
    const own = rowsFor.get(entry.id) || [];
    const extra = (adopted[entry.id] || [])
      .filter((k) => !own.some((r) => r.key === k))
      .map((k) => byKey.get(k))
      .filter((r): r is Row => !!r && !r.retired);
    return extra.length ? [...own, ...extra] : own;
  }, [entry, rowsFor, adopted, byKey]);
  const state: StateDef | null = entry && entry.states.length ? entry.states[Math.min(stateIdx, entry.states.length - 1)] : null;

  /**
   * 🔴 A PICK THAT HAS ALREADY CHOSEN ITS STATE MUST NOT BE OVERRIDDEN BY THESE TWO EFFECTS.
   *
   * Both run after the render a pick causes, in this order, and both used to act on it: the
   * first reset the state to 0 because the entry changed, the second (reading the PREVIOUS
   * entry's stateIdx) jumped to the first state that draws the key. So a click could land on
   * a different state — a different page in the frame — from the one the click came from.
   * `pendingStateIdx` carries the pick's own choice through the reset, and `selPlaced` tells
   * the second effect the pick already put the frame where it belongs.
   */
  const pendingStateIdx = React.useRef<number | null>(null);
  const selPlaced = React.useRef<string | null>(null);
  React.useEffect(() => {
    setStateIdx(pendingStateIdx.current ?? 0);
    pendingStateIdx.current = null;
    setQ("");
  }, [entryId]);

  // Selecting a string that lives in a different state switches to that state —
  // the design's rule that no string is reachable only by clicking around.
  React.useEffect(() => {
    if (sel && selPlaced.current === sel) { selPlaced.current = null; return; }
    selPlaced.current = null;
    if (!sel || !entry || !entry.states.length) return;
    const cur = entry.states[Math.min(stateIdx, entry.states.length - 1)];
    if (keysInBlocks(cur.blocks).includes(sel)) return;
    const idx = entry.states.findIndex((s) => keysInBlocks(s.blocks).includes(sel));
    if (idx >= 0) setStateIdx(idx);
    // Intentionally keyed on `sel` alone — re-running on every row change
    // would yank the state pills around while typing.
  }, [sel]);

  const valueOf = React.useCallback(
    (key: string) => (edits[key] !== undefined ? edits[key] : byKey.get(key)?.text ?? ""),
    [edits, byKey],
  );

  /**
   * CLICK THE WORDS IN THE PREVIEW, LAND ON THE FIELD.
   *
   * Dustin, 2026-09-10: *"right now I have to search and dig and if I'm on the wrong page,
   * nothing searchable shows."* The drawn phone has always been clickable; the live one now
   * reports which keys could have produced the words that were tapped (see the tracker's
   * lib/preview.ts), and this turns that into a selection.
   *
   * Three things have to happen, and skipping any one of them leaves him searching:
   *  1. NARROW THE CANDIDATES. Text is not unique — "Back" is four keys — so prefer one that
   *     belongs to the screen he is already on, and only then fall back to the first known.
   *  2. GO TO ITS SCREEN. If the key lives on another rail entry, switch to it; otherwise
   *     `setSel` points at a field that is not rendered and nothing appears to happen.
   *  3. CLEAR THE SEARCH BOX. A leftover filter can hide the very row we just selected, which
   *     looks exactly like the click having failed.
   */
  // The Worker serves the app under a mount (`/preview`); the frame reports its own pathname,
  // the layout names routes without it.
  const previewRootPath = React.useMemo(() => {
    const root = layout?.preview_base?.split("|")[0];
    if (!root) return "";
    try { return new URL(root).pathname.replace(/\/+$/, ""); } catch { return ""; }
  }, [layout?.preview_base]);

  const pickFromPreview = React.useCallback(
    (keys: string[], live?: PickInfo) => {
      const known = keys.filter((k) => byKey.has(k));
      if (!known.length) {
        // The app drew a line this editor has never heard of: it shipped new wording and the
        // import hasn't caught up. Say so, rather than a click that silently does nothing.
        if (keys.length) setToast("That line is new in the app and isn't in the editor yet — press “Check for new app text”.");
        return;
      }
      const shows = (id: string | undefined, k: string) =>
        !!id && ((rowsFor.get(id) || []).some((r) => r.key === k) || (adopted[id] || []).includes(k));
      const draws = (e: ScreenDef | null | undefined, k: string) => !!e?.states?.some((st) => keysInBlocks(st.blocks).includes(k));
      const sameScreen = (k: string) => !!entry && byKey.get(k)?.screen === entry.screen;
      const key =
        known.find((k) => shows(entry?.id, k)) ??
        known.find((k) => draws(entry, k)) ??
        // "The Binder" is both `binder.title` (Today) and `claim.opt.binder` (Claim); on Today
        // the Today one is the one he is looking at.
        known.find(sameScreen) ??
        known[0];

      /**
       * 🔴 THE SCREEN BEING EDITED WINS WHEN THE CLICK CAME FROM IT.
       *
       * Dustin, 2026-09-13: clicking "Heavy Metal ⌄" on Today jumped the dashboard to another
       * screen. The live frame IS that screen, and it draws far more than the rail entry's
       * layout lists — the detox chip, "CURRENT", the tab bar, the binder block — so the old
       * "does this entry list the key?" test failed for lines that were plainly on it, and the
       * pick fell through to whichever entry happened to list the key first (and then to its
       * state 0, a different page). A click in an un-walked frame is proof the line is on the
       * screen being edited, so we stay: select it here, adding its field to this screen's panel
       * if the layout never listed it.
       *
       * Once a press has gone through (`walked`), the frame may be on another page or have a
       * sheet open, so that proof is gone — then only a line this entry really has keeps us here.
       */
      if (live && entry && (!live.walked || shows(entry.id, key))) {
        if (!shows(entry.id, key)) setAdopted((a) => ({ ...a, [entry.id]: [...(a[entry.id] || []), key] }));
        // The line is visible in the frame right now: don't let the state effect reload it.
        if (key !== sel) selPlaced.current = key;
        setQ("");
        setSel(key);
        return;
      }

      const rawPath = live?.path ?? null;
      const path = rawPath && previewRootPath && rawPath.startsWith(previewRootPath)
        ? rawPath.slice(previewRootPath.length) || "/"
        : rawPath;
      const statesAt = (e: ScreenDef) => e.states.filter((st) => (st as any).preview?.path === path && keysInBlocks(st.blocks).includes(key));
      const owner = shows(entry?.id, key)
        ? entry
        : entries.find((e) => !!path && statesAt(e).length > 0 && shows(e.id, key))
          ?? entries.find((e) => draws(e, key) && shows(e.id, key))
          ?? entries.find((e) => shows(e.id, key));
      if (owner && owner.id !== entryId) {
        // Pick the state here rather than letting the effects guess: where the frame was, else
        // the first state that draws the line, else the first.
        const at = path ? owner.states.findIndex((st) => (st as any).preview?.path === path && keysInBlocks(st.blocks).includes(key)) : -1;
        const drawn = owner.states.findIndex((st) => keysInBlocks(st.blocks).includes(key));
        pendingStateIdx.current = Math.max(0, at >= 0 ? at : drawn);
        if (key !== sel) selPlaced.current = key;
        setEntryId(owner.id);
      }
      setQ("");
      setSel(key);
    },
    [byKey, rowsFor, adopted, entries, entry, entryId, sel, previewRootPath],
  );

  // Search hits on every screen but this one, labelled with the screen that holds each field.
  const findElsewhere = React.useCallback((needle: string) => {
    const here = new Set(entryRows.map((r) => r.key));
    return all
      .filter((r) => !r.retired && !here.has(r.key) && rowMatches(r, needle))
      .slice(0, 40)
      .map((row) => ({
        row,
        label: entries.find((e) => (rowsFor.get(e.id) || []).some((x: Row) => x.key === row.key))?.label || row.screen,
      }));
  }, [all, entryRows, entries, rowsFor]);

  // ---------------- sync new lines from the app
  async function syncFromApp() {
    setBusy("sync");
    const res = await fetch("/api/app-content/pull", { method: "POST" })
      .then(async (r) => ({ status: r.status, body: await r.json().catch(() => null) })).catch(() => null);
    setBusy(null);
    const b = res?.body;
    if (!b || res?.status === 403) {
      setToast(res?.status === 403 ? "You don't have permission to sync App Copy." : "Couldn't reach the dashboard — try again.");
      return;
    }
    if (!b.ok) {
      setToast(b.reason === "not_configured"
        ? "The app connection isn't set up on this dashboard."
        : "Couldn't reach the app to check for new text — nothing changed. Try again in a minute.");
      await load();
      return;
    }
    await load();
    // The screen list came back too: reload it so new preview states appear without a page refresh.
    fetch("/api/app-layout").then((r) => (r.ok ? r.json() : null))
      .then((d) => setLayout(d?.layout ? { ...d.layout, preview_base: d.preview_base ?? null } : null)).catch(() => {});
    const n = b.inserted || 0;
    // Label/description refreshes are counted in the audit log but not announced — "3 labels
    // refreshed" means nothing to the person editing wording.
    const parts = [
      n ? `${n} new line${n === 1 ? "" : "s"} added` : null,
      b.retired ? `${b.retired} line${b.retired === 1 ? "" : "s"} the app no longer uses moved to “No longer in the app”` : null,
    ].filter(Boolean);
    if (b.layout && !b.layout.pulled) parts.push("couldn't refresh the app's screen list, so the preview may be out of date — try again in a minute");
    setToast(parts.length ? parts.join(" · ") + "." : "Already up to date — every line and screen in the app is here.");
  }

  const ctx: PhoneCtx = {
    value: valueOf,
    has: (k) => byKey.has(k),
    sel,
    // The drawn phone goes through the same route as the live one, so a line drawn here whose
    // field lives on another entry still lands on it.
    setSel: (k) => (k ? pickFromPreview([k]) : setSel(null)),
    markers,
    vars: varsFor(detox, binderDay),
    keyVars: KEY_VARS,
    theme,
    // On a binder day the app swaps the jar for the jar-plus-Binder combo shot.
    hero: (synced?.manifest?.theme?.detox?.[detox.id]?.hero as string)
      || (binderDay && detox.binder && detox.combo ? detox.combo : detox.hero),
    product: detox.name,
  };

  // ---------------- saving
  async function save(key: string, next: { text?: string; approved?: boolean }) {
    const row = byKey.get(key);
    if (!row) return;
    if (next.text !== undefined) {
      if (!next.text.trim()) {
        // Blank = "delete this line", but only where the app says the screen survives
        // without it. Everywhere else it would leave an unlabelled control behind.
        if (!row.erasable) {
          setErrors((e) => ({ ...e, [key]: "This line can't be removed — the app needs it. Only supporting lines (captions, footnotes, subtitles) can be deleted." }));
          return;
        }
        setErrors((e) => { const n = { ...e }; delete n[key]; return n; });
      } else {
      // Checked against the app's BUILT-IN wording, never against what's published —
      // the app validates overrides the same way, and a stale published line would
      // otherwise block the edit that fixes it. Server re-checks; this is for speed.
      const declared = placeholdersOf(row.builtin ?? liveOf(row));
      const used = placeholdersOf(next.text);
      const missing = declared.filter((p) => !used.includes(p));
      const invented = used.filter((p) => !declared.includes(p));
      if (missing.length) {
        setErrors((e) => ({ ...e, [key]: `This line needs ${missing.join(" and ")} in it — the app fills that in.` }));
        return;
      }
      if (invented.length) {
        setErrors((e) => ({ ...e, [key]: `The app doesn't fill ${invented.join(" or ")} here — it would ignore the whole line. Use only ${declared.join(", ") || "plain wording"}.` }));
        return;
      }
      }
    }
    setErrors((e) => { const n = { ...e }; delete n[key]; return n; });
    setSaving((s) => ({ ...s, [key]: true }));
    const res = await fetch(`/api/app-content/${encodeURIComponent(key)}`, {
      method: "PUT", headers: { "content-type": "application/json" }, body: JSON.stringify(next),
    }).then(async (r) => ({ ok: r.ok, body: await r.json().catch(() => null) })).catch(() => null);
    setSaving((s) => { const n = { ...s }; delete n[key]; return n; });
    if (!res?.ok || !res.body?.row) {
      setErrors((e) => ({ ...e, [key]: res?.body?.detail || "Couldn't save — try again." }));
      return;
    }
    const saved = res.body.row as Row;
    setRows((prev) => (prev || []).map((r) => (r.key === key ? saved : r)));
    setEdits((e) => { const n = { ...e }; if (n[key] === saved.text) delete n[key]; return n; });
  }

  async function revert(key: string) {
    const res = await fetch(`/api/app-content/${encodeURIComponent(key)}/revert`, { method: "POST" })
      .then((r) => (r.ok ? r.json() : null)).catch(() => null);
    if (!res?.row) { setToast("Couldn't revert that line."); return; }
    setRows((prev) => (prev || []).map((r) => (r.key === key ? res.row : r)));
    setEdits((e) => { const n = { ...e }; delete n[key]; return n; });
    setErrors((e) => { const n = { ...e }; delete n[key]; return n; });
  }

  async function doPublish() {
    setConfirm(null);
    setBusy("publish");
    const res = await fetch("/api/app-content/publish", { method: "POST" })
      .then(async (r) => ({ status: r.status, body: r.ok ? await r.json() : null })).catch(() => null);
    setBusy(null);
    if (!res?.body) {
      setToast(res?.status === 403 ? "Only the owner can publish. Your edits are saved." : "Publish failed — nothing was sent to the app.");
      return;
    }
    await load();
    setToast(res.body.changed
      ? `${res.body.changed} string${res.body.changed === 1 ? "" : "s"} published — live in the app within a minute (v${res.body.version}). Ticks cleared.`
      : "Nothing to publish — no approved line has changed.");
  }

  // Discard = put every changed line on THIS screen back to what the app shows.
  async function doDiscard() {
    setConfirm(null);
    const targets = entryRows.filter((r) => isChanged(r) || edits[r.key] !== undefined);
    setBusy("discard");
    for (const r of targets) await revert(r.key);
    setBusy(null);
    setToast(`${targets.length} line${targets.length === 1 ? "" : "s"} put back to what the app shows.`);
  }

  async function approveAllOnScreen() {
    const targets = entryRows.filter((r) => isChanged(r) && !r.approved);
    setBusy("approve");
    for (const r of targets) await save(r.key, { approved: true });
    setBusy(null);
  }

  const refusedAll = all.filter((r) => r.refused && !r.retired);
  // 🔴 RETIRED LINES ARE NEVER "CHANGES" (2026-09-27). A line the app stopped using has an
  // empty built-in, so its old wording always differed from "live" — 38 of them inflated
  // "46 waiting for approval" to Dustin while only 8 were real. The server's publish already
  // skips them (pendingKeys: retired = 0), so counting them also made the Publish dialog
  // promise changes that would never go out.
  const pendingAll = all.filter((r) => !r.retired && isPending(r));
  const changedAll = all.filter((r) => !r.retired && isChanged(r));
  const needsApproval = changedAll.filter((r) => !r.approved);

  if (rows === null) {
    return <Card><div style={{ fontFamily: FIGTREE, fontSize: 13, color: t.muted }}>Loading app copy…</div></Card>;
  }

  const rail = (
    <ScreenRail
      entries={entries} rowsFor={rowsFor} entryId={entry?.id || ""} onPick={setEntryId} isMobile={isMobile}
    />
  );

  const stage = (
    <Stage
      entry={entry} state={state} stateIdx={stateIdx} setStateIdx={setStateIdx}
      ctx={ctx} markers={markers} setMarkers={setMarkers} isMobile={isMobile}
      detoxes={detoxes} detox={detox} setDetoxId={setDetoxId}
      binderDay={binderDay} setBinderDay={setBinderDay} synced={synced} syncedScreens={syncedScreens}
      previewBase={layout?.preview_base ?? null} drafts={drafts}
      onPickKey={pickFromPreview}
    />
  );

  const panel = (
    <CopyPanel
      entry={entry} rows={entryRows} q={q} setQ={setQ}
      valueOf={valueOf} setEdits={setEdits} save={save} revert={revert}
      errors={errors} saving={saving} sel={sel} setSel={setSel}
      meta={meta} pending={pendingAll.length} needsApproval={needsApproval.length}
      onPublish={() => setConfirm("publish")} onDiscard={() => setConfirm("discard")}
      onApproveScreen={approveAllOnScreen} busy={busy} isMobile={isMobile}
      onHelp={() => setHelp(true)}
      sync={sync} onSync={syncFromApp}
      findElsewhere={findElsewhere} goToKey={(k: string) => pickFromPreview([k])}
    />
  );

  return (
    <>
      <div style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "214px minmax(0,1fr) minmax(340px,384px)",
        gap: 14,
        alignItems: "stretch",
        // The phone is sized FROM this height, so every pixel here is phone. 124 = the 64px top
        // bar + main's 28px top padding + a 32px foot; the negative margin hands the rest of
        // main's 48px bottom padding back so the page itself doesn't scroll.
        height: isMobile ? undefined : "calc(100vh - 124px)",
        marginBottom: isMobile ? undefined : -16,
        minHeight: isMobile ? undefined : 620,
      }}>
        {rail}
        {stage}
        {panel}
      </div>

      {!!refusedAll.length && (
        <div style={{
          position: "fixed", left: "50%", bottom: 22, transform: "translateX(-50%)", zIndex: 60,
          background: t.surface, border: "1px solid " + t.bad, borderRadius: 999,
          padding: "9px 16px", display: "flex", alignItems: "center", gap: 9,
          boxShadow: "0 10px 30px rgba(0,0,0,0.45)", maxWidth: "92vw",
        }}>
          <Icon name="alert" size={14} color={t.bad} />
          <span style={{ fontFamily: FIGTREE, fontSize: 12, color: t.fg2 }}>
            The app is ignoring {refusedAll.length} published line{refusedAll.length === 1 ? "" : "s"} —
          </span>
          <button onClick={() => { setEntryId((entries.find((e) => (rowsFor.get(e.id) || []).some((r: Row) => r.refused)) || entries[0])?.id || ""); setSel(refusedAll[0].key); }}
            style={{ background: "transparent", border: "none", cursor: "pointer", fontFamily: FIGTREE, fontWeight: 700, fontSize: 12, color: t.accent, padding: 0 }}>
            show me
          </button>
        </div>
      )}

      {help && <HowThisWorks onClose={() => setHelp(false)} />}

      {confirm === "publish" && (
        <Modal onClose={() => setConfirm(null)}>
          <Title size={17}>Publish {pendingAll.length} approved change{pendingAll.length === 1 ? "" : "s"} to the app?</Title>
          <div style={{ fontFamily: FIGTREE, fontSize: 13, color: t.muted, marginTop: 8, lineHeight: 1.55 }}>
            Customers see the new wording the next time the app opens, and it stays live from then on. Only ticked
            lines go — everything else stays exactly as it is. The ticks clear once these are out.
          </div>
          <div style={{ marginTop: 12, maxHeight: 190, overflowY: "auto", display: "grid", gap: 6 }}>
            {pendingAll.slice(0, 14).map((r) => (
              <div key={r.key} style={{ fontFamily: FIGTREE, fontSize: 12, color: t.fg2 }}>
                <span style={{ color: t.faint }}>{r.screen} · {splitWhere(r.where).label} — </span>“{r.text}”
              </div>
            ))}
            {pendingAll.length > 14 && <div style={{ fontFamily: FIGTREE, fontSize: 12, color: t.faint }}>…and {pendingAll.length - 14} more.</div>}
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
            <Btn variant="ghost" onClick={() => setConfirm(null)}>Cancel</Btn>
            <Btn variant="solid" tone="accent" icon="send" onClick={doPublish}>Publish to the app</Btn>
          </div>
        </Modal>
      )}

      {confirm === "discard" && (
        <Modal onClose={() => setConfirm(null)}>
          <Title size={17}>Discard the edits on {entry?.label}?</Title>
          <div style={{ fontFamily: FIGTREE, fontSize: 13, color: t.muted, marginTop: 8, lineHeight: 1.55 }}>
            Every changed line on this screen goes back to what the app is showing right now. Published wording isn't
            touched, and no other screen is affected. This can't be undone.
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
            <Btn variant="ghost" onClick={() => setConfirm(null)}>Keep editing</Btn>
            <Btn variant="solid" tone="bad" icon="refresh" onClick={doDiscard}>Discard edits</Btn>
          </div>
        </Modal>
      )}

      {toast && (
        <div style={{
          position: "fixed", left: "50%", bottom: 26, transform: "translateX(-50%)", zIndex: 90,
          background: t.surface, border: "1px solid " + t.borderStrong, borderRadius: 999,
          padding: "11px 20px", fontFamily: FIGTREE, fontSize: 13, color: t.fg,
          boxShadow: "0 10px 30px rgba(0,0,0,0.45)", maxWidth: "90vw", textAlign: "center",
        }}>{toast}</div>
      )}
    </>
  );
}

// ============================================================ RAIL
function ScreenRail({ entries, rowsFor, entryId, onPick, isMobile }: any) {
  const t = useT();
  const flows: { flow: string; items: ScreenDef[] }[] = [];
  for (const f of [...FLOW_ORDER, ...entries.map((e: ScreenDef) => e.flow)]) {
    if (flows.some((x) => x.flow === f)) continue;
    const items = entries.filter((e: ScreenDef) => e.flow === f);
    if (items.length) flows.push({ flow: f, items });
  }

  if (isMobile) {
    return (
      <Card pad={12}>
        <select
          value={entryId} onChange={(e) => onPick(e.target.value)}
          style={{ width: "100%", padding: "10px 12px", borderRadius: 10, background: t.surface2, color: t.fg, border: "1px solid " + t.borderStrong, fontFamily: FIGTREE, fontSize: 13.5 }}
        >
          {flows.map((g) => (
            <optgroup key={g.flow} label={g.flow}>
              {g.items.map((e) => <option key={e.id} value={e.id}>{e.label} ({(rowsFor.get(e.id) || []).length})</option>)}
            </optgroup>
          ))}
        </select>
      </Card>
    );
  }

  return (
    <Card pad={0} style={{ overflow: "hidden", display: "flex", flexDirection: "column", minHeight: 0 }}>
      <div style={{ padding: "14px 16px 8px", fontFamily: OSWALD, fontWeight: 700, fontSize: 10.5, letterSpacing: "0.16em", textTransform: "uppercase", color: t.faint }}>
        Screens
      </div>
      <div style={{ overflowY: "auto", padding: "0 8px 12px", flex: 1, minHeight: 0 }}>
        {flows.map((g) => (
          <div key={g.flow} style={{ marginBottom: 10 }}>
            <div style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 10, letterSpacing: "0.13em", textTransform: "uppercase", color: t.faint, padding: "8px 10px 5px" }}>{g.flow}</div>
            {g.items.map((e) => {
              const rs: Row[] = rowsFor.get(e.id) || [];
              const changed = rs.filter((r) => !r.retired && isChanged(r)).length;
              const on = e.id === entryId;
              const preview = e.states.length > 0;
              return (
                <button key={e.id} onClick={() => onPick(e.id)} style={{
                  width: "100%", textAlign: "left", cursor: "pointer", display: "flex", gap: 9, alignItems: "center",
                  padding: "9px 10px", borderRadius: 10, marginBottom: 2,
                  background: on ? t.surface2 : "transparent",
                  border: "1px solid " + (on ? t.borderStrong : "transparent"),
                }}>
                  <span style={{
                    width: 20, height: 26, borderRadius: 4, flexShrink: 0,
                    background: preview ? "linear-gradient(180deg,#f9dc63,#dda205)" : "rgba(150,130,230,0.16)",
                    opacity: preview ? 1 : 0.7,
                  }} />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: "block", fontFamily: FIGTREE, fontWeight: on ? 700 : 600, fontSize: 12.5, color: on ? t.fg : t.fg2, lineHeight: 1.25 }}>{e.label}</span>
                    <span style={{ display: "block", fontFamily: FIGTREE, fontSize: 10.5, color: t.faint, marginTop: 2 }}>
                      {rs.length} string{rs.length === 1 ? "" : "s"}{preview ? "" : " · no preview"}
                    </span>
                  </span>
                  {!!changed && (
                    <span style={{ flexShrink: 0, minWidth: 18, textAlign: "center", borderRadius: 999, background: t.accentSoft, color: t.accent, fontFamily: FIGTREE, fontWeight: 700, fontSize: 10.5, padding: "2px 6px" }}>{changed}</span>
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </Card>
  );
}

// ============================================================ STAGE
function Stage({ entry, state, stateIdx, setStateIdx, ctx, markers, setMarkers, isMobile, detoxes, detox, setDetoxId, binderDay, setBinderDay, synced, syncedScreens, previewBase, drafts, onPickKey }: any) {
  // A state that says where it lives in the real app gets the app, not a drawing of it.
  // Everything else keeps its blocks — see StateDef.preview in the tracker's screens.ts.
  const liveSrc = previewBase && state?.preview ? previewUrl(previewBase, state.preview) : null;
  const t = useT();
  const boxRef = React.useRef<HTMLDivElement | null>(null);
  const [box, setBox] = React.useState({ w: 560, h: 760 });
  const [zoom, setZoom] = React.useState<"fit" | "full">("fit");
  const [device, setDevice] = React.useState<DeviceId>("standard");
  // The drawn phone is always the standard size; only the real app can be re-laid-out.
  const dev = liveSrc ? DEVICES[device] : DEVICES.standard;

  React.useEffect(() => {
    const el = boxRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const read = () => setBox({ w: el.clientWidth, h: el.clientHeight });
    const ro = new ResizeObserver(read);
    ro.observe(el);
    read();
    return () => ro.disconnect();
  }, []);

  /**
   * A real phone's points, scaled — never reflowed, so it stays a real phone's proportions.
   *
   * 🔴 FIT ON BOTH AXES. This used to divide by HEIGHT alone, so the phone kept its size as
   * the middle column narrowed and simply hung out over the edge — Dustin, twice: *"certain
   * screens are giant. Some of them run clear off the page."* A picture that leaves the pane
   * is not a preview of anything. Whichever axis runs out first decides the scale.
   *
   * "100%" pins the phone at its TRUE size (390×844, or 430×932 for the 6.7″) on any window and
   * lets the pane scroll instead of shrinking the picture — same reason the flow map got a zoom.
   */
  //
  // 🔴 NOTHING BUT THE PHONE SITS UNDER THE PHONE (2026-09-13). The state's caption and the
  // "this IS the app" note used to take ~70px below it, which is height the phone could not
  // use — on a laptop that made it about 60% of true size. They live behind the ⓘ beside the
  // "Showing" picker now, and the phone gets the whole pane. The ceiling is a little above
  // 1 so a big monitor gets a phone you can read from your chair; the 100% button is still
  // the real size.
  const PAD_Y = 20, PAD_X = 24;
  const compact = box.w < 560;
  const fit = Math.min(1.2, Math.max(0.34, Math.min((box.h - PAD_Y) / dev.h, (box.w - PAD_X) / dev.w)));
  const scale = zoom === "full" ? 1 : fit;

  // Everything the caption used to say, for the ⓘ.
  const provenance: { text: string; tone: "good" | "muted" } = liveSrc
    ? { text: `This IS the app — the same code the phone runs${synced?.app_version ? ` (${synced.app_version})` : ""}. Not a drawing of it, so it cannot disagree with the phone.`, tone: "good" }
    : !synced
      ? { text: "Approximate layout — colours and spacing are the dashboard's stand-in until the app pushes its own design. Wording is real.", tone: "muted" }
      : syncedScreens
        ? { text: `Layout + colours from the app itself${synced.app_version ? ` (${synced.app_version})` : ""} · design last changed ${ago(synced.received_at)}`, tone: "muted" }
        : { text: `Colours, palette and detox facts from the app itself${synced.app_version ? ` (${synced.app_version})` : ""} · design last changed ${ago(synced.received_at)}. The arrangement of the screen is still drawn here.`, tone: "muted" };
  const caption = state
    ? state.caption || (liveSrc ? "The real app, running. Your unpublished edits show here before you publish them." : "")
    : "";

  return (
    <Card pad={0} style={{
      overflow: "hidden", display: "flex", flexDirection: "column",
      /**
       * 🔴 A DEFINITE HEIGHT ON NARROW SCREENS, not a minimum.
       *
       * With `minHeight` the pane grew to fit its contents, and its contents are the phone —
       * whose size is computed FROM the pane's height. That loop only settles at the ceiling,
       * so on a laptop-narrow or tablet window the phone was pinned at max scale inside a
       * column too narrow to hold it, and hung out over the edge: *"certain screens are
       * giant. Some of them run clear off the page."* A fixed viewport-relative height breaks
       * the circle — the pane is sized by the window, and the phone is sized by the pane.
       */
      height: isMobile ? "min(74vh, 640px)" : undefined,
      minHeight: 0,
    }}>
      {/* Toolbar: two dropdowns, not a wall of pills — there are up to eight states
          on a screen and four detoxes, which is a list, not a tab bar. */}
      {/* Every wrapped toolbar row is height taken from the phone, so related controls stay
          glued together (state + ⓘ, detox + binder day) and labels drop on a narrow column. */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", padding: "8px 12px", borderBottom: "1px solid " + t.border }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, minWidth: 0 }}>
          {entry?.states?.length > 1 && (
            <Picker
              label={compact ? "" : "Showing"}
              value={String(stateIdx)}
              onChange={(v: string) => setStateIdx(parseInt(v, 10))}
              options={entry.states.map((st: StateDef, i: number) => ({ value: String(i), label: st.label }))}
            />
          )}
          {state && (
            <InfoTip
              // A new state is a new explanation — close a pinned popover rather than leave the
              // last state's caption sitting over this one.
              key={`${entry?.id}:${stateIdx}`}
              title={state.label}
              caption={caption}
              hint={liveSrc ? "Click any line on the phone to jump to its field on the right. Click the same link or button again to use it in the app." : "Click any text on the screen to jump to its field."}
              provenance={provenance}
            />
          )}
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <Picker
            label={compact ? "" : "Detox"}
            value={detox.id}
            onChange={setDetoxId}
            options={detoxes.map((d: Detox) => ({ value: d.id, label: `${d.name}${d.binder ? "" : " · no binder"}` }))}
          />
          <CheckBox
            on={binderDay && detox.binder}
            disabled={!detox.binder}
            title={detox.binder ? "Every other day on this detox" : "The Binder isn't taken on Heavy Metal"}
            onClick={() => setBinderDay(!binderDay)}
            label="Binder day"
          />
        </span>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
          {/* Read the small print without squinting. "Fit" is the honest default — the whole
              screen at once — and 100% is the phone at its real size, scrolled. */}
          {liveSrc && (
            <Picker
              label={compact ? "" : "Phone"}
              value={device}
              onChange={(v: string) => setDevice(v as DeviceId)}
              options={(Object.keys(DEVICES) as DeviceId[]).map((id) => ({ value: id, label: DEVICES[id].label }))}
            />
          )}
          <Zoom value={zoom} onChange={setZoom} pct={Math.round(fit * 100)} />
          <CheckBox on={markers} onClick={() => setMarkers(!markers)} label={compact ? "Markers" : "String markers"} />
        </div>
      </div>

      {/* the phone sits on the dashboard's own purple so it reads as a device, not a panel */}
      <div ref={boxRef} style={{
        flex: 1, minHeight: 0, overflow: "auto", display: "flex", flexDirection: "column",
        // `center` only while it FITS. Centring something wider than its box pushes the
        // overflow out of the left edge, where no amount of scrolling reaches it.
        alignItems: scale * dev.w + PAD_X > box.w ? "flex-start" : "center",
        justifyContent: "flex-start", padding: `${PAD_Y / 2}px ${PAD_X / 2}px`,
        backgroundColor: t.appBg,
        backgroundImage: "repeating-linear-gradient(118deg, rgba(255,255,255,0.02) 0 1px, transparent 1px 26px), radial-gradient(120% 70% at 50% -10%, #2a2159 0%, #1a1438 52%, #100c24 100%)",
      }}>
        {state ? (
          <>
            {liveSrc ? (
              <LiveFrame src={liveSrc} drafts={drafts} scale={scale} onPick={onPickKey} selected={ctx.sel} device={device} />
            ) : (
              <Phone ctx={ctx} chrome={state.chrome} blocks={state.blocks} scale={scale} tabHighlight={state.tabHighlight} />
            )}
          </>
        ) : (
          <div style={{ maxWidth: 320, textAlign: "center", margin: "auto", display: "grid", gap: 8 }}>
            <Icon name="doc" size={26} color="rgba(255,255,255,0.35)" style={{ margin: "0 auto" }} />
            <div style={{ fontFamily: OSWALD, fontWeight: 700, fontSize: 16, color: "rgba(255,255,255,0.8)", letterSpacing: "0.02em" }}>No preview drawn yet</div>
            <div style={{ fontFamily: FIGTREE, fontSize: 12.5, color: "rgba(255,255,255,0.5)", lineHeight: 1.5 }}>
              {entry?.note || "These strings are still fully editable on the right — only the picture is missing."}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

// ---------------- toolbar controls

/**
 * ⓘ beside the state picker — what this state is, and how true the picture is.
 *
 * Hover reads it; click (or tap, where there is no hover) pins it open until you click
 * elsewhere or press Escape. It replaced two always-on lines under the phone that cost the
 * phone its height (Dustin, 2026-09-13: make the preview bigger, put the caption behind an icon).
 */
function InfoTip({ title, caption, hint, provenance }: {
  title: string; caption: string; hint: string; provenance: { text: string; tone: "good" | "muted" };
}) {
  const t = useT();
  const [hover, setHover] = React.useState(false);
  const [pinned, setPinned] = React.useState(false);
  const wrap = React.useRef<HTMLSpanElement | null>(null);
  const pop = React.useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = React.useState<{ top: number; left: number } | null>(null);
  const open = hover || pinned;

  // 🔴 RENDERED INTO document.body, NOT BESIDE THE BUTTON. The preview column is its own
  // stacking/clipping context, so an absolutely-positioned tooltip was cut off at the column's
  // edge no matter its z-index (Dustin, 2026-09-13: "it's getting cut off in the window").
  // A portal with fixed positioning sits above everything; the position is re-measured on
  // open, scroll and resize, and clamped inside the viewport.
  const WIDTH = 360;
  const place = React.useCallback(() => {
    const r = wrap.current?.getBoundingClientRect();
    if (!r) return;
    const left = Math.max(12, Math.min(r.left - 8, window.innerWidth - WIDTH - 12));
    setPos({ top: r.bottom + 8, left });
  }, []);

  React.useEffect(() => {
    if (!open) return;
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => { window.removeEventListener("scroll", place, true); window.removeEventListener("resize", place); };
  }, [open, place]);

  React.useEffect(() => {
    if (!pinned) return;
    const inside = (n: Node) => !!(wrap.current?.contains(n) || pop.current?.contains(n));
    const onDown = (e: MouseEvent) => { if (!inside(e.target as Node)) setPinned(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setPinned(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [pinned]);

  return (
    <span ref={wrap} style={{ position: "relative", display: "inline-flex" }}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <button
        onClick={() => setPinned((p) => !p)}
        aria-label="About this screen"
        aria-expanded={open}
        style={{
          cursor: "pointer", width: 24, height: 24, borderRadius: 999, padding: 0, lineHeight: 1,
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          background: open ? t.accentSoft : "transparent",
          border: "1px solid " + (open ? t.accent : t.borderStrong),
          fontFamily: "Georgia, 'Times New Roman', serif", fontStyle: "italic", fontWeight: 700, fontSize: 13,
          color: open ? t.accent : t.muted,
        }}
      >i</button>
      {open && pos && ReactDOM.createPortal(
        <div ref={pop} role="tooltip"
          onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
          style={{
            position: "fixed", top: pos.top, left: pos.left, zIndex: 2147483000,
            width: WIDTH, maxWidth: "calc(100vw - 24px)", maxHeight: "calc(100vh - " + (pos.top + 12) + "px)", overflowY: "auto",
            boxSizing: "border-box", padding: "12px 14px",
            background: t.surface, border: "1px solid " + t.borderStrong, borderRadius: 12,
            boxShadow: "0 14px 36px rgba(0,0,0,0.45)", display: "grid", gap: 8, textAlign: "left",
          }}>
          <div style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: t.faint }}>
            {title}
          </div>
          {caption && <div style={{ fontFamily: FIGTREE, fontSize: 12.5, lineHeight: 1.5, color: t.fg2 }}>{caption}</div>}
          <div style={{ fontFamily: FIGTREE, fontSize: 12, lineHeight: 1.45, color: t.fg, fontWeight: 600 }}>{hint}</div>
          <div style={{ fontFamily: FIGTREE, fontSize: 11, lineHeight: 1.45, color: provenance.tone === "good" ? t.good : t.faint, borderTop: "1px solid " + t.border, paddingTop: 8 }}>
            {provenance.text}
          </div>
        </div>,
        document.body,
      )}
    </span>
  );
}

function Picker({ label, value, onChange, options }: any) {
  const t = useT();
  return (
    <label style={{ display: "inline-flex", alignItems: "center", gap: 7, background: t.surface2, border: "1px solid " + t.borderStrong, borderRadius: 999, padding: "5px 6px 5px 12px" }}>
      {label && <span style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: t.faint }}>{label}</span>}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          background: "transparent", border: "none", outline: "none", cursor: "pointer",
          fontFamily: FIGTREE, fontWeight: 700, fontSize: 12.5, color: t.fg, padding: "3px 4px", maxWidth: label ? 240 : 190,
        }}
      >
        {options.map((o: any) => (
          <option key={o.value} value={o.value} style={{ background: "#1d1740", color: "#f4f1ff" }}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}

/** Fit-to-pane or the phone's real 390×844. Two states, so a segmented pair, not a slider. */
function Zoom({ value, onChange, pct }: { value: "fit" | "full"; onChange: (v: "fit" | "full") => void; pct: number }) {
  const t = useT();
  const btn = (v: "fit" | "full", label: string, title: string) => (
    <button
      onClick={() => onChange(v)}
      title={title}
      style={{
        cursor: "pointer", border: "none", padding: "4px 9px", borderRadius: 6,
        fontFamily: FIGTREE, fontSize: 11.5, fontWeight: 700,
        background: value === v ? t.accent : "transparent",
        color: value === v ? "#2a1508" : t.muted,
      }}
    >
      {label}
    </button>
  );
  return (
    <div style={{ display: "inline-flex", gap: 2, padding: 2, borderRadius: 8, border: "1px solid " + t.border }}>
      {btn("fit", "Fit", `Whole screen at once — currently ${pct}%`)}
      {btn("full", "100%", "The phone at its real size. The pane scrolls.")}
    </div>
  );
}

function CheckBox({ on, onClick, label, disabled, title }: any) {
  const t = useT();
  return (
    <button onClick={disabled ? undefined : onClick} title={title} disabled={disabled} style={{
      cursor: disabled ? "not-allowed" : "pointer", background: "transparent", border: "none", padding: "4px 2px",
      display: "inline-flex", alignItems: "center", gap: 7, opacity: disabled ? 0.45 : 1, whiteSpace: "nowrap",
      fontFamily: FIGTREE, fontSize: 11.5, fontWeight: 700, color: on ? t.accent : t.muted,
    }}>
      <span style={{
        width: 16, height: 16, borderRadius: 4, border: "1px solid " + (on ? t.accent : t.borderStrong),
        background: on ? t.accent : "transparent", display: "inline-flex", alignItems: "center", justifyContent: "center",
      }}>{on && <Icon name="check" size={11} color="#2a1508" />}</span>
      {label}
    </button>
  );
}

// ============================================================ COPY PANEL
function CopyPanel({
  entry, rows, q, setQ, valueOf, setEdits, save, revert, errors, saving, sel, setSel, findElsewhere, goToKey,
  meta, pending, needsApproval, onPublish, onDiscard, onApproveScreen, busy, isMobile, onHelp, sync, onSync,
}: any) {
  const t = useT();
  const refs = React.useRef<Record<string, HTMLDivElement | null>>({});
  const scrollRef = React.useRef<HTMLDivElement | null>(null);

  // Phone → panel: scroll the selected field into view and focus it.
  React.useEffect(() => {
    if (!sel) return;
    const el = refs.current[sel];
    const box = scrollRef.current;
    if (!el || !box) return;
    box.scrollTo({ top: Math.max(0, el.offsetTop - 90), behavior: "smooth" });
    const input = el.querySelector("textarea,input") as HTMLTextAreaElement | null;
    if (input && document.activeElement !== input) input.focus({ preventScroll: true });
  }, [sel]);

  const needle = fold(q.trim());
  const shown: Row[] = rows.filter((r: Row) => rowMatches(r, needle));
  /**
   * 🔴 SEARCH IS NOT CONFINED TO THE SCREEN YOU'RE ON.
   *
   * It used to be, so a key from a fix list typed while on Today said "Nothing matches that" —
   * indistinguishable from the line not existing. Matches on other screens are listed under
   * the ones here; picking one goes to its screen and selects its field.
   */
  const elsewhere: { row: Row; label: string }[] = needle ? findElsewhere(needle) : [];

  const groups: { group: string; items: Row[] }[] = [];
  for (const r of shown) {
    const { group } = splitWhere(r.where);
    const g = groups.find((x) => x.group === group);
    if (g) g.items.push(r); else groups.push({ group, items: [r] });
  }

  const screenChanged = rows.filter((r: Row) => isChanged(r)).length;
  const screenNeedsApproval = rows.filter((r: Row) => isChanged(r) && !r.approved).length;

  return (
    <Card pad={0} style={{ overflow: "hidden", display: "flex", flexDirection: "column", minHeight: isMobile ? 520 : 0 }}>
      <div style={{ padding: "13px 14px 11px", borderBottom: "1px solid " + t.border }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
          <Title size={14}>{entry?.label || "Copy"}</Title>
          <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontFamily: FIGTREE, fontSize: 11, color: t.faint }}>
              {rows.length} string{rows.length === 1 ? "" : "s"}
            </span>
            {/* Quiet on purpose — there when you want it, not in the way. */}
            <button onClick={onHelp} title="How editing and publishing works" style={{
              cursor: "pointer", background: "transparent", border: "1px solid " + t.borderStrong,
              borderRadius: 999, width: 19, height: 19, padding: 0, lineHeight: 1,
              fontFamily: FIGTREE, fontWeight: 700, fontSize: 11, color: t.muted,
            }}>?</button>
          </span>
        </div>
        <div style={{ position: "relative", marginTop: 9 }}>
          <Icon name="search" size={14} color={t.faint} style={{ position: "absolute", left: 10, top: 9 }} />
          <input
            value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a string or key…"
            style={{
              width: "100%", boxSizing: "border-box", padding: "8px 11px 8px 30px", borderRadius: 999,
              border: "1px solid " + t.borderStrong, background: t.surface2, color: t.fg,
              fontFamily: FIGTREE, fontSize: 12.5, outline: "none",
            }}
          />
        </div>
        {/* New wording in the app arrives on its own every 15 minutes; this is the "now" button,
            and the line beside it says when the last check happened — and says so plainly if
            the last check FAILED, which used to be invisible. */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
          <span
            title={sync ? `Last check: ${new Date(sync.at).toLocaleString()} — ${sync.reason}${sync.version ? ` (app copy ${sync.version})` : ""}` : "No check recorded yet"}
            style={{ flex: 1, minWidth: 0, fontFamily: FIGTREE, fontSize: 10.5, color: sync && !sync.ok ? t.bad : t.faint, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
          >
            {!sync
              ? "App text not checked yet"
              : sync.ok
                ? `App text checked ${ago(sync.at)}`
                : `Last check failed ${ago(sync.at)}`}
          </span>
          <button onClick={onSync} disabled={busy === "sync"} title="Pull any new lines the app has shipped into this editor. Never changes your wording." style={{
            flexShrink: 0, cursor: busy === "sync" ? "wait" : "pointer", background: "transparent",
            border: "1px solid " + t.borderStrong, borderRadius: 999, padding: "4px 10px",
            display: "inline-flex", alignItems: "center", gap: 5,
            fontFamily: FIGTREE, fontWeight: 700, fontSize: 11, color: t.fg2, opacity: busy === "sync" ? 0.6 : 1,
          }}>
            <Icon name="refresh" size={12} color={t.muted} />
            {busy === "sync" ? "Checking…" : "Check for new app text"}
          </button>
        </div>
      </div>

      <div ref={scrollRef} style={{ flex: 1, minHeight: 0, overflowY: "auto", position: "relative" }}>
        {!shown.length && !elsewhere.length && (
          <div style={{ padding: 16, fontFamily: FIGTREE, fontSize: 12.5, color: t.muted }}>Nothing matches that on any screen.</div>
        )}
        {!shown.length && !!elsewhere.length && (
          <div style={{ padding: "12px 14px 4px", fontFamily: FIGTREE, fontSize: 12, color: t.muted }}>
            Not on {entry?.label || "this screen"} — found on {elsewhere.length === 1 ? "another screen" : "other screens"}:
          </div>
        )}
        {groups.map((g) => (
          <div key={g.group}>
            <div style={{
              position: "sticky", top: 0, zIndex: 2, background: t.surface2,
              padding: "7px 14px", fontFamily: FIGTREE, fontWeight: 700, fontSize: 10,
              letterSpacing: "0.13em", textTransform: "uppercase", color: t.muted,
              borderBottom: "1px solid " + t.border,
            }}>{g.group}</div>
            {g.items.map((r) => (
              <Field
                key={r.key} row={r} value={valueOf(r.key)} selected={sel === r.key}
                error={errors[r.key]} saving={!!saving[r.key]}
                bind={(el: HTMLDivElement | null) => { refs.current[r.key] = el; }}
                onFocus={() => setSel(r.key)}
                onChange={(v: string) => setEdits((e: any) => ({ ...e, [r.key]: v }))}
                onCommit={(v: string) => save(r.key, { text: v })}
                onApprove={(dirty: boolean, v: string) => save(r.key, dirty ? { text: v, approved: !r.approved } : { approved: !r.approved })}
                onRevert={() => revert(r.key)}
                onUseApp={() => { setEdits((e: any) => ({ ...e, [r.key]: r.builtin })); save(r.key, { text: r.builtin }); }}
              />
            ))}
          </div>
        ))}
        {!!elsewhere.length && (
          <div>
            {!!shown.length && (
              <div style={{
                position: "sticky", top: 0, zIndex: 2, background: t.surface2,
                padding: "7px 14px", fontFamily: FIGTREE, fontWeight: 700, fontSize: 10,
                letterSpacing: "0.13em", textTransform: "uppercase", color: t.muted,
                borderBottom: "1px solid " + t.border,
              }}>On other screens</div>
            )}
            {elsewhere.map(({ row: r, label }) => (
              <button key={r.key} onClick={() => goToKey(r.key)} style={{
                display: "block", width: "100%", textAlign: "left", cursor: "pointer",
                background: "transparent", border: "none", borderBottom: "1px solid " + t.border, padding: "10px 14px",
              }}>
                <span style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                  <span style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 12.5, color: t.fg, flex: 1, minWidth: 0 }}>
                    {label} <span style={{ fontWeight: 500, color: t.muted }}>· {r.where}</span>
                  </span>
                  <Icon name="chevron" size={12} color={t.accent} />
                </span>
                <span style={{ display: "block", fontFamily: FIGTREE, fontSize: 12, color: t.fg2, marginTop: 3, lineHeight: 1.4 }}>“{r.text}”</span>
                <span style={{ display: "block", fontFamily: MONO, fontSize: 10, color: t.faint, marginTop: 3 }}>{r.key}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ---------------- publish bar ---------------- */}
      <div style={{
        borderTop: "1px solid " + t.border, padding: "11px 14px",
        background: pending ? t.accentSoft : t.surface2,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 12.5, color: pending ? t.accent : t.fg2 }}>
              {pending ? `${pending} ready to publish` : needsApproval ? `${needsApproval} waiting for approval` : "No changes"}
            </div>
            <div style={{ fontFamily: FIGTREE, fontSize: 10.5, color: t.faint, marginTop: 2 }}>
              App on v{meta?.version ?? 1} · published {ago(meta?.published_at ?? null)}
              {meta?.published_by ? ` by ${String(meta.published_by).split("@")[0]}` : ""}
            </div>
          </div>
          <Btn variant="ghost" size="sm" disabled={!screenChanged || busy === "discard"} onClick={onDiscard}>Discard</Btn>
          <Btn variant="solid" tone="accent" size="sm" icon="send" disabled={!pending || busy === "publish"} onClick={onPublish}>
            {busy === "publish" ? "Publishing…" : "Publish"}
          </Btn>
        </div>
        {!!screenNeedsApproval && (
          <button onClick={onApproveScreen} disabled={busy === "approve"} style={{
            marginTop: 8, width: "100%", cursor: "pointer", borderRadius: 999, padding: "7px 10px",
            background: "transparent", border: "1px dashed " + t.borderStrong,
            fontFamily: FIGTREE, fontWeight: 700, fontSize: 11, color: t.muted,
          }}>
            {busy === "approve" ? "Approving…" : `Approve the ${screenNeedsApproval} changed line${screenNeedsApproval === 1 ? "" : "s"} on this screen`}
          </button>
        )}
      </div>
    </Card>
  );
}

// ============================================================ ONE FIELD
function Field({ row, value, selected, error, saving, bind, onFocus, onChange, onCommit, onApprove, onRevert, onUseApp }: any) {
  const t = useT();
  const { label } = splitWhere(row.where);
  const live = liveOf(row);
  const dirty = value !== row.text;
  const changed = isChanged(row, row.text);
  const tokens = placeholdersOf(live);
  const multiline = live.length > 44 || value.length > 44 || value.includes("\n");
  const capsWarn = isAllCaps(live) && !!value.trim() && !isAllCaps(value);
  const overBy = value.length - live.length;

  const inputStyle: React.CSSProperties = {
    width: "100%", boxSizing: "border-box", padding: "8px 10px", borderRadius: 9,
    border: "1px solid " + (error ? t.bad : selected ? t.accent : dirty ? t.accent : t.borderStrong),
    background: t.surface2, color: t.fg, fontFamily: FIGTREE, fontSize: 13, lineHeight: 1.45, outline: "none",
    resize: "vertical",
  };

  return (
    <div ref={bind} style={{
      padding: "11px 14px", borderBottom: "1px solid " + t.border,
      background: selected ? "rgba(248,146,92,0.07)" : "transparent",
      borderLeft: "2px solid " + (selected ? t.accent : "transparent"),
    }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <div style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 12.5, color: t.fg, flex: 1, minWidth: 0 }}>
          {changed && <span style={{ display: "inline-block", width: 6, height: 6, borderRadius: 999, background: t.accent, marginRight: 6, verticalAlign: "middle" }} />}
          {label}
        </div>
        {row.erasable && (
          <span title="Supporting line — clearing it removes it from the app" style={{
            flexShrink: 0, fontFamily: FIGTREE, fontWeight: 700, fontSize: 9,
            letterSpacing: "0.08em", textTransform: "uppercase", color: t.faint,
            border: "1px solid " + t.border, borderRadius: 4, padding: "1px 4px",
          }}>can be removed</span>
        )}
        <div style={{ fontFamily: MONO, fontSize: 10, color: overBy > Math.max(8, live.length * 0.25) ? t.warn : t.faint, flexShrink: 0 }}>
          {value.length}{live ? ` / ${live.length}` : ""}
        </div>
      </div>

      <div style={{ marginTop: 6 }}>
        {multiline ? (
          <textarea
            value={value} rows={Math.min(6, Math.max(2, Math.ceil(value.length / 42)))}
            onChange={(e) => onChange(e.target.value)}
            onFocus={onFocus}
            onBlur={() => { if (dirty) onCommit(value); }}
            onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") (e.target as HTMLTextAreaElement).blur(); }}
            style={inputStyle}
          />
        ) : (
          <input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onFocus={onFocus}
            onBlur={() => { if (dirty) onCommit(value); }}
            onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
            style={inputStyle}
          />
        )}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
        <span style={{ fontFamily: MONO, fontSize: 10, color: t.faint }}>{row.key}</span>
        {tokens.map((tok) => {
          const kept = value.includes(tok);
          return (
            <span key={tok} title={kept ? "The app fills this in" : "Put this back — the app needs it"} style={{
              fontFamily: MONO, fontSize: 10, borderRadius: 5, padding: "2px 5px",
              background: kept ? t.purpleSoft : t.badSoft, color: kept ? t.purple : t.bad,
            }}>{tok}</span>
          );
        })}
        <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 8 }}>
          {saving && <span style={{ fontFamily: FIGTREE, fontSize: 10.5, color: t.faint }}>Saving…</span>}
          {changed && (
            <button onClick={onRevert} title="Put this line back to what the app shows now" style={{
              background: "transparent", border: "none", cursor: "pointer", padding: 0,
              fontFamily: FIGTREE, fontSize: 10.5, color: t.muted, display: "inline-flex", alignItems: "center", gap: 3,
            }}>
              <Icon name="refresh" size={11} color={t.muted} />Revert
            </button>
          )}
          <button onClick={() => onApprove(dirty, value)} style={{
            display: "inline-flex", alignItems: "center", gap: 5, cursor: "pointer", background: "transparent", border: "none", padding: 0,
          }}>
            <span style={{
              width: 15, height: 15, borderRadius: 4, flexShrink: 0,
              border: "1px solid " + (row.approved ? t.good : t.borderStrong),
              background: row.approved ? t.good : "transparent",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
            }}>{row.approved && <Icon name="check" size={11} color="#10221a" />}</span>
            <span style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 10.5, color: row.approved ? t.fg2 : t.muted }}>Approved</span>
          </button>
        </span>
      </div>

      {/*
        🔴 THE APP'S OWN WORDING, WHEN A PUBLISHED LINE HIDES IT.

        A published line overrides the app forever — so when the app is re-worded (a feature
        changed, a promise it no longer keeps), customers keep seeing the old text and nothing
        here said so. Revert can't fix that: it goes back to what was PUBLISHED. This shows the
        app's current wording and puts it in the field in one click; approve + publish as usual.
      */}
      {row.published != null && row.builtin && row.builtin !== row.published && value !== row.builtin && (
        <div style={{
          marginTop: 7, padding: "7px 9px", borderRadius: 8,
          background: t.surface2, border: "1px dashed " + t.borderStrong,
          fontFamily: FIGTREE, fontSize: 11, lineHeight: 1.45, color: t.muted,
        }}>
          <span style={{ fontWeight: 700, color: t.fg2 }}>The app’s own wording: </span>“{row.builtin}”
          <button onClick={onUseApp} style={{
            display: "block", marginTop: 4, background: "transparent", border: "none", cursor: "pointer", padding: 0,
            fontFamily: FIGTREE, fontWeight: 700, fontSize: 10.5, color: t.accent,
          }}>Use the app’s wording</button>
        </div>
      )}

      {row.erasable && !value.trim() && (
        <div style={{
          marginTop: 6, padding: "7px 9px", borderRadius: 8,
          background: t.warnSoft, border: "1px solid " + t.warn,
          fontFamily: FIGTREE, fontSize: 11, lineHeight: 1.45, color: t.warn,
        }}>
          <span style={{ fontWeight: 700 }}>Blank = this line is removed. </span>
          The app drops it and closes the gap. Publish it like any other change; type something to put it back.
        </div>
      )}
      {row.refused && (
        <div style={{
          marginTop: 6, padding: "7px 9px", borderRadius: 8,
          background: t.badSoft, border: "1px solid " + t.bad,
          fontFamily: FIGTREE, fontSize: 11, lineHeight: 1.45, color: t.bad,
        }}>
          <span style={{ fontWeight: 700 }}>The app is ignoring this line. </span>{row.refused} Re-word it and publish again.
        </div>
      )}
      {row.notes && <div style={{ fontFamily: FIGTREE, fontSize: 11, color: t.faint, marginTop: 5, lineHeight: 1.4 }}>{row.notes}</div>}
      {row.when && row.when !== "Always" && (
        <div style={{ fontFamily: FIGTREE, fontSize: 11, color: t.faint, marginTop: 4, lineHeight: 1.4 }}>
          <span style={{ color: t.muted, fontWeight: 700 }}>Shows when: </span>{row.when}
        </div>
      )}
      {error && <div style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.bad, marginTop: 5 }}>{error}</div>}
      {!error && capsWarn && (
        <div style={{ fontFamily: FIGTREE, fontSize: 11, color: t.warn, marginTop: 5 }}>
          This line is ALL CAPS in the app — it ships exactly as typed, no auto-capitalising.
        </div>
      )}
      {changed && (
        <div style={{ fontFamily: FIGTREE, fontSize: 11, color: t.faint, marginTop: 5 }}>App shows now: “{live}”</div>
      )}
    </div>
  );
}

// ============================================================ HOW THIS WORKS
// The reference Dustin asked for: not front and centre, but one click from the
// panel header whenever "wait, does Approved stay ticked?" comes up at 11pm.
function HowThisWorks({ onClose }: { onClose: () => void }) {
  const t = useT();
  const H = ({ children }: any) => (
    <div style={{ fontFamily: OSWALD, fontWeight: 700, fontSize: 13, letterSpacing: "0.06em", textTransform: "uppercase", color: t.fg, marginTop: 18, marginBottom: 7 }}>{children}</div>
  );
  const P = ({ children, style }: any) => (
    <div style={{ fontFamily: FIGTREE, fontSize: 13, lineHeight: 1.6, color: t.fg2, marginBottom: 8, ...style }}>{children}</div>
  );
  const B = ({ children }: any) => <span style={{ color: t.fg, fontWeight: 700 }}>{children}</span>;
  const Step = ({ n, children }: any) => (
    <div style={{ display: "flex", gap: 11, alignItems: "flex-start", marginBottom: 9 }}>
      <span style={{
        width: 21, height: 21, borderRadius: 999, flexShrink: 0, background: t.accentSoft, color: t.accent,
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        fontFamily: FIGTREE, fontWeight: 800, fontSize: 11,
      }}>{n}</span>
      <div style={{ fontFamily: FIGTREE, fontSize: 13, lineHeight: 1.55, color: t.fg2 }}>{children}</div>
    </div>
  );

  return (
    <Modal onClose={onClose}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <div style={{ flex: 1 }}>
          <Title size={18}>How editing and publishing works</Title>
          <div style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.muted, marginTop: 4 }}>
            Nothing on this screen reaches a customer until you publish it.
          </div>
        </div>
        <button onClick={onClose} style={{ cursor: "pointer", background: "transparent", border: "none", padding: 4 }}>
          <Icon name="x" size={16} color={t.muted} />
        </button>
      </div>

      <H>Getting a change live — three steps</H>
      <Step n={1}>Edit the text. It saves when you click away. <B>This changes nothing for customers</B> — it's a working copy, and it survives closing the tab.</Step>
      <Step n={2}>Tick <B>Approved</B> on the lines you want to go out. Untouched or half-written lines simply don't get ticked.</Step>
      <Step n={3}>Hit <B>Publish</B>. Only ticked lines that actually changed are sent. Customers see the new wording the next time the app opens.</Step>

      <H>Does publishing un-tick Approved?</H>
      <P><B>Yes.</B> Everything that goes out gets its tick cleared, and the version number goes up.</P>
      <P>
        So a ticked box always means one thing: <B>this edit is waiting to go out</B>. Empty boxes and a new version
        number mean it shipped. If you edit that same line again later, you tick it again — nothing can ever ship an
        edit you didn't approve that time.
      </P>

      <H>Deleting a line</H>
      <P>
        Some lines can be removed outright: clear the box, leave it empty, and publish. The app drops the line and
        closes the gap. Those rows are marked <B>can be removed</B> next to their name, and the box tells you what a
        blank means before you publish it.
      </P>
      <P>
        <B>The app decides which ones.</B> Supporting text can go — captions, footnotes, eyebrows, subtitles, grey
        lines. Anything that names or explains a control stays: buttons, links, field labels, titles. Blanking a
        button would leave an invisible thing to tap, so those refuse to save empty. Small grey links look like
        footnotes but are deliberately protected — each one is the only way to reach something.
      </P>

      <H>The smaller rules</H>
      <P>· A ticked line you haven't changed is <B>skipped</B>. A publish with nothing to send doesn't burn a version number.</P>
      <P>· <B>Un-ticking a box before you publish</B> just takes that line out of the next publish. It does nothing to wording that's already live.</P>
      <P>· <B>Revert</B> puts one line back to exactly what the app is showing now. <B>Discard</B> does that for every changed line on the screen you're looking at.</P>
      <P>· You can't save a line that drops a <B>{"{PLACEHOLDER}"}</B> — the app fills those in, and losing one leaves a hole in the sentence. The chips under each field turn red when one is missing.</P>
      <P>· A line the app shows in ALL CAPS warns you if you type it in lower case. That's a warning, not a block — the app ships exactly what you type.</P>

      <H>What happens if the dashboard goes down</H>
      <P>
        <B>Nothing you've published changes.</B> Published wording is stored here and copied onto each phone the
        first time the app fetches it, so the app keeps showing your wording whether or not this dashboard, its
        database, or the network is up. It does not roll back to the app's original text.
      </P>
      <P>
        The wording built into the app is the floor for one case only: <B>lines you have never published</B> — plus a
        brand-new install that has never once reached us. That's what stops a customer ever seeing a blank space or a
        raw key like <span style={{ fontFamily: MONO, fontSize: 12 }}>today.cta.track</span>.
      </P>
      <P>· Edits you've saved but not published are just sitting in the working copy. An outage does nothing to them either — they're still here when it's back.</P>

      <H>When does the app's design come across?</H>
      <P>
        The <B>wording</B> here is always yours. The <B>layout and colours</B> in the preview belong to the app, and
        the dashboard asks the app for them: once when you open this screen (if what it has is more than five minutes
        old) and every fifteen minutes in the background. So a design change you make in the app shows up here on a
        refresh — you don't push anything.
      </P>
      <P>
        The app can also send its design the moment it deploys, if the tracker side prefers that. Either way, the
        line under the phone tells you which you're looking at: the app's real design and when it last came across,
        or the dashboard's approximation because the app hasn't sent one yet.
      </P>

      <H>About the phone preview</H>
      <P>
        The wording on the phone is real — it's your working copy, updating as you type. The <B>layout and colours
        are the dashboard's approximation</B> until the app itself publishes its design; the line under the phone
        tells you which you're looking at. The <B>Detox</B> and <B>Binder day</B> pickers change who the screen is
        for, not what's saved.
      </P>

      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
        <Btn variant="solid" tone="accent" onClick={onClose}>Got it</Btn>
      </div>
    </Modal>
  );
}

// ============================================================ modal
function Modal({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  const t = useT();
  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, zIndex: 95, background: "rgba(6,4,18,0.62)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 18,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: t.surface, border: "1px solid " + t.borderStrong, borderRadius: t.cardRadius,
        padding: 22, width: "min(560px, 100%)", maxHeight: "80vh", overflowY: "auto",
      }}>{children}</div>
    </div>
  );
}
